import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Download,
  FileText,
  Building,
  User,
  Calendar,
  IndianRupee,
  AlertCircle,
  Mail,
  Phone
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { formatDate } from '../../../utils/stringUtils';
import { API_ENDPOINTS } from '../../../api/config';

const PublicQuotationView = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('t');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [contact, setContact] = useState(null);
  const [items, setItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showFullTerms, setShowFullTerms] = useState(false);
  const [showFullNotes, setShowFullNotes] = useState(false);

  // Fetch quotation data
  useEffect(() => {
    const fetchQuotation = async () => {
      if (!quoteId) {
        setError('Quotation ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Build URL with token if available
        const url = token
          ? `${API_ENDPOINTS.PUBLIC_QUOTE}/${quoteId}?t=${encodeURIComponent(token)}`
          : `${API_ENDPOINTS.PUBLIC_QUOTE}/${quoteId}`;

        // Public routes don't need auth token
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.responseCode === 200 && data.data) {
          // For public view, use the quotation document directly as returned from quotations collection
          const quotationData = data.data.quote || data.data.quotation || data.data;
          setQuotation(quotationData);
          // Customer/contact data comes from billingAddress/shippingAddress (enriched with lead data from backend)
          const billing = quotationData.billingAddress || {};
          const shipping = quotationData.shippingAddress || {};
          setCustomer({
            companyName: billing.company || shipping.company || '',
            email: billing.email || shipping.email || '',
            phone: billing.phone || shipping.phone || ''
          });
          setContact({
            firstName: billing.firstName || shipping.firstName || '',
            lastName: billing.lastName || shipping.lastName || '',
            email: billing.email || shipping.email || '',
            phone: billing.phone || shipping.phone || '',
            designation: ''
          });
          // Map items to only include required fields: Product Name, Quantity, MRP, Unit Price, Expiry Date, Manufacturer, Discount %, Tax %, Line Total
          const mappedItems = (quotationData.items || []).map((item) => ({
            productName: item.productName || '',
            quantity: item.quantity || 0,
            mrp: item.mrp || 0,
            unitPrice: item.unitPrice || 0,
            expiry: item.expiry || null,
            manufacturing: item.manufacturing || '',
            discount: item.discount || 0,
            tax: item.tax || 0,
            lineTotal: item.lineTotal || 0
          }));
          setItems(mappedItems);
          // Set financial totals from backend (do not calculate)
          setSubtotal(quotationData.subtotal || 0);
          setDiscountAmount(quotationData.discountAmount || 0);
          setTaxAmount(quotationData.taxAmount || 0);
          setTotalAmount(quotationData.totalAmount || 0);
          // Note: Attachments/PDF are only downloaded when user clicks the button (no auto-open on load)
        } else {
          setError(data.responseMessage || 'Failed to load quotation');
        }
      } catch (err) {

        setError(err.response?.data?.responseMessage || err.message || 'Failed to load quotation');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotation();
  }, [quoteId, token]);

  // Handle accept click (show confirmation)
  const handleAcceptClick = () => {
    if (!quotation) return;

    if (quotation.status === 'accepted') {
      alert('You have already accepted this quotation');
      return;
    }

    if (quotation.status === 'rejected') {
      alert('You have already rejected this quotation. Cannot accept a rejected quotation.');
      return;
    }

    setShowAcceptConfirm(true);
  };

  // Handle accept confirmation
  const handleAccept = async () => {
    if (!quotation || submitting) return;

    setShowAcceptConfirm(false);
    setSubmitting(true);

    try {
      const url = token
        ? `${API_ENDPOINTS.PUBLIC_QUOTE}/${quoteId}/accept?t=${encodeURIComponent(token)}`
        : `${API_ENDPOINTS.PUBLIC_QUOTE}/${quoteId}/accept`;

      // Public routes don't need auth token
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.responseCode === 200) {
        // Update local state
        setQuotation(prev => ({
          ...prev,
          status: 'accepted',
          acceptedDate: data.data?.quotation?.acceptedDate || new Date().toISOString()
        }));
        // Success message will be shown via status badge, no alert needed
      } else {
        alert(data.responseMessage || 'Failed to accept quotation');
      }
    } catch (err) {

      alert(err.response?.data?.responseMessage || err.message || 'Failed to accept quotation');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reject click (show rejection reason modal)
  const handleRejectClick = () => {
    if (!quotation) return;

    if (quotation.status === 'rejected') {
      alert('You have already rejected this quotation');
      return;
    }

    if (quotation.status === 'accepted') {
      alert('You have already accepted this quotation. Cannot reject an accepted quotation.');
      return;
    }

    setRejectReason(''); // Reset rejection reason
    setShowRejectReasonModal(true);
  };

  // Handle reject confirmation with reason
  const handleReject = async () => {
    if (!quotation || submitting || !rejectReason.trim()) return;

    setShowRejectReasonModal(false);
    setSubmitting(true);

    try {
      const url = token
        ? `${API_ENDPOINTS.PUBLIC_QUOTE}/${quoteId}/reject?t=${encodeURIComponent(token)}`
        : `${API_ENDPOINTS.PUBLIC_QUOTE}/${quoteId}/reject`;

      // Public routes don't need auth token
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejectReason: rejectReason.trim()
        })
      });

      const data = await response.json();

      if (data.responseCode === 200) {
        // Update local state
        setQuotation(prev => ({
          ...prev,
          status: 'rejected',
          rejectedDate: data.data?.quotation?.rejectedDate || new Date().toISOString(),
          rejectReason: rejectReason.trim()
        }));
        // Success message will be shown via status badge, no alert needed
      } else {
        alert(data.responseMessage || 'Failed to reject quotation');
      }
    } catch (err) {

      alert(err.response?.data?.responseMessage || err.message || 'Failed to reject quotation');
    } finally {
      setSubmitting(false);
      setRejectReason(''); // Reset rejection reason
    }
  };

  // Handle attachment download/view (PDF or image)
  const ensureFilenameHasExtension = (filename, mimeType = '') => {
    if (!filename) return 'attachment';

    const mimeToExtension = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/webp': '.webp',
      'text/plain': '.txt'
    };

    if (/\.[a-z0-9]+$/i.test(filename)) return filename;
    return `${filename}${mimeToExtension[mimeType] || ''}`;
  };

  const filenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const last = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-z0-9]+$/i.test(last) ? last : '';
    } catch {
      return '';
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSingleAttachment = async (url, name, fallbackPrefix = 'attachment', meta = {}) => {
    const baseName = name || filenameFromUrl(url) || fallbackPrefix;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch file');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const resolvedMimeType = blob.type || meta.mimeType || '';
      const finalName = ensureFilenameHasExtension(baseName, resolvedMimeType);
      triggerBrowserDownload(blobUrl, finalName);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      // Fallback for CORS-restricted URLs
      triggerBrowserDownload(url, ensureFilenameHasExtension(baseName, meta.mimeType || ''));
    }
  };

  const handleDownloadAttachment = async () => {
    if (!quotation) {
      alert('No attachment available');
      return;
    }

    const attachments = [];

    // Add all uploaded attachment URLs
    if (Array.isArray(quotation.attachments)) {
      quotation.attachments.forEach((att) => {
        if (att?.url) {
          attachments.push({
            url: att.url,
            name: att.filename || att.originalName || att.name || att.fileName || '',
            mimeType: att.mimeType || ''
          });
        }
      });
    }

    // Add PDF URL if present (and not already included)
    const pdfUrl = quotation.pdfUrl || quotation?.pdf?.url;
    if (pdfUrl && !attachments.some((att) => att.url === pdfUrl)) {
      attachments.push({
        url: pdfUrl,
        name: quotation.quoteNumber ? `${quotation.quoteNumber}.pdf` : 'quotation.pdf'
      });
    }

    if (!attachments.length) {
      alert('No attachment available');
      return;
    }

    for (let index = 0; index < attachments.length; index += 1) {
      const attachment = attachments[index];
      await downloadSingleAttachment(
        attachment.url,
        attachment.name,
        `attachment-${index + 1}`,
        attachment
      );
    }
  };

  // Get attachment button text and check if attachment exists
  const getAttachmentInfo = () => {
    const attachmentCount = Array.isArray(quotation?.attachments)
      ? quotation.attachments.filter((att) => !!att.url).length
      : 0;
    const pdfUrl = quotation?.pdfUrl || quotation?.pdf?.url;
    const totalCount = attachmentCount + (pdfUrl ? 1 : 0);

    return {
      text:
        totalCount > 0
          ? `Download ${totalCount === 1 ? 'Attachment' : 'Attachments'} (${totalCount})`
          : 'Download Attachment',
      available: totalCount > 0
    };
  };


  // Get expiry date - use expiryDate if available, otherwise calculate from validityDays for backward compatibility
  const getExpiryDate = () => {
    if (!quotation) return null;

    // First try to use expiryDate directly
    if (quotation.expiryDate) {
      try {
        const expiryDate = new Date(quotation.expiryDate);
        if (!isNaN(expiryDate.getTime())) {
          return expiryDate;
        }
      } catch (error) {

      }
    }

    return null;
  };

  const expiryDate = getExpiryDate();

  // Check if quotation is expired
  const isExpired = !!(
    quotation?.isExpired ||
    (expiryDate && new Date() > expiryDate && !['accepted', 'rejected', 'order', 'ordered'].includes(quotation?.status))
  );

  const getStatusBadge = () => {
    if (!quotation) return null;

    if (isExpired) {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-orange-800 break-words">Quotation Expired</h3>
              <p className="text-sm text-orange-700 break-words">
                This quotation expired on {expiryDate ? formatDate(expiryDate, { includeTime: false }) : 'N/A'} and is no longer valid.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (['accepted', 'order', 'ordered'].includes(quotation.status)) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-green-800 break-words">Quotation Accepted</h3>
              <p className="text-sm text-green-700 break-words">
                This quotation has been accepted on {quotation.acceptedDate ? formatDate(new Date(quotation.acceptedDate), { includeTime: false }) : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (quotation.status === 'rejected') {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-red-800 break-words">Quotation Rejected</h3>
              <p className="text-sm text-red-700 break-words">
                This quotation has been rejected on {quotation.rejectedDate ? formatDate(new Date(quotation.rejectedDate), { includeTime: false }) : 'N/A'}
              </p>
              {quotation.rejectReason && (
                <div className="mt-3 p-3 bg-red-100 rounded-md">
                  <p className="text-sm font-medium text-red-800 mb-1">Rejection Reason:</p>
                  <p className="text-sm text-red-700 break-words">{quotation.rejectReason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-200 rounded-lg p-12 w-full max-w-2xl">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-48 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
            <div className="space-y-3 mt-8">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-red-200 rounded-lg p-6 max-w-md w-full">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
              <p className="text-sm text-red-700 break-words">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md w-full">
          <p className="text-gray-600 break-words">Quotation not found</p>
        </div>
      </div>
    );
  }

  // Derive customer & contact details from quotation addresses (enriched with lead data from backend)
  const billing = quotation.billingAddress || {};
  const shipping = quotation.shippingAddress || {};
  const hasCustomerObject =
    customer && typeof customer === 'object' && (customer.companyName || customer.name || customer.contactPerson);

  const primaryCompany =
    quotation.billingAddress?.company ||
    quotation.shippingAddress?.company ||
    billing.company ||
    shipping.company ||
    (hasCustomerObject ? (customer.companyName || customer.name) : '') ||
    '';

  const primaryContactName = (() => {
    if (contact?.firstName || contact?.lastName) {
      return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    }
    if (hasCustomerObject && customer?.contactPerson) {
      return customer.contactPerson;
    }
    const firstName = billing.firstName || shipping.firstName || '';
    const lastName = billing.lastName || shipping.lastName || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return '';
  })();

  const primaryEmail =
    contact?.email ||
    quotation?.email ||
    billing.email ||
    shipping.email ||
    (hasCustomerObject ? customer.email : '') ||
    '';

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';

    // Handle array case
    if (Array.isArray(phone)) {
      return phone.filter(Boolean).join(', ');
    }

    const phoneStr = String(phone).trim();
    if (!phoneStr) return 'N/A';

    // If it's a long numeric string (e.g., 20 digits for 2 numbers)
    // or multiples of 10 digits, split into 10-digit chunks
    if (/^\d{20,100}$/.test(phoneStr)) {
      const matches = phoneStr.match(/.{1,10}/g);
      return matches ? matches.join(', ') : phoneStr;
    }

    return phoneStr;
  };

  const primaryPhone = formatPhone(
    (hasCustomerObject && customer.phone) ||
    quotation.shippingAddress?.phone ||
    billing.phone ||
    shipping.phone ||
    ''
  );



  // Helper to render city/state which can be string or object
  const renderLocation = (location) => {
    if (!location) return '';
    return typeof location === 'object' ? location.name : location;
  };



  // Use financial totals from backend (do not calculate)
  const taxableAmount = subtotal - discountAmount;
  const grandTotal = totalAmount;

  const canShowActions = quotation && !isExpired &&
    ['draft', 'finalized', 'sent', 'seen'].includes(quotation.status);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
          {/* Company Header */}
          <div className="bg-gradient-to-r from-agrosaf-green to-agrosaf-darkGreen px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-white p-2 rounded-lg shadow-sm">
                  <img
                    src="/logo.png"
                    alt="Company Logo"
                    className="h-10 w-10 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {process.env.REACT_APP_COMPANY_NAME}
                  </h2>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {(() => {
                  const attachmentInfo = getAttachmentInfo();
                  return attachmentInfo.available ? (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Download className="h-4 w-4" />}
                      onClick={handleDownloadAttachment}
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    >
                      {attachmentInfo.text}
                    </Button>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          {/* Quotation Details */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 break-words">{quotation.title}</h1>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-600">Quote Number:</span>
                  <span className="font-semibold text-agrosaf-green">{quotation.quoteNumber}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status and Date Info */}
          <div className="px-6 py-3 bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">Valid Till:</span>
                  <span className="font-medium text-gray-900">
                    {expiryDate ? formatDate(expiryDate, { includeTime: false }) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        {getStatusBadge()}

        {/* Customer Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3 min-w-0">
              <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600">Company Name</p>
                <p className="font-medium text-gray-900 break-words capitalize">
                  {primaryCompany || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 min-w-0">
              <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600">Customer Name</p>
                <p className="font-medium text-gray-900 break-words capitalize">
                  {primaryContactName || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 min-w-0">
              <Mail className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-gray-900 break-words">
                  {primaryEmail || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 min-w-0">
              <Phone className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium text-gray-900 break-words">
                  {primaryPhone || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 min-w-0">
              <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600">Valid Till</p>
                <p className="font-medium text-gray-900 break-words">
                  {expiryDate ? formatDate(expiryDate, { includeTime: false }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Billing & Shipping Addresses */}
        {(quotation.billingAddress || quotation.shippingAddress) && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Addresses</h2>
            <div className="space-y-4">
              {/* Billing Address - full row */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      Billing Address
                    </span>
                  </div>
                  {quotation.billingAddress?.gstin && (
                    <span className="text-xs font-medium text-gray-600 uppercase break-words">
                      GSTIN: {quotation.billingAddress.gstin}
                    </span>
                  )}
                  {quotation.billingAddress?.drugLicenseNumber && (
                    <span className="text-xs font-medium text-gray-600 uppercase break-words ml-4">
                      Drug License: {quotation.billingAddress.drugLicenseNumber}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Company</p>
                    <p className="font-medium capitalize break-words">
                      {quotation.billingAddress?.company ||
                        customer?.companyName ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">House no. / Street</p>
                    <p className="capitalize break-words">
                      {quotation.billingAddress?.address ||
                        quotation.billingAddress?.street ||
                        customer?.billingAddress?.address ||
                        customer?.billingAddress?.street ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">City</p>
                    <p className="capitalize break-words">
                      {renderLocation(quotation.billingAddress?.city) ||
                        renderLocation(customer?.billingAddress?.city) ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">State</p>
                    <p className="capitalize break-words">
                      {renderLocation(quotation.billingAddress?.state) ||
                        renderLocation(customer?.billingAddress?.state) ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Pincode</p>
                    <p className="break-words">
                      {quotation.billingAddress?.pincode ||
                        customer?.billingAddress?.pincode ||
                        'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipping Address - full row */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      Shipping Address
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Company</p>
                    <p className="font-medium capitalize break-words">
                      {quotation.shippingAddress?.company ||
                        quotation.billingAddress?.company ||
                        customer?.companyName ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">House no. / Street</p>
                    <p className="capitalize break-words">
                      {quotation.shippingAddress?.address ||
                        quotation.shippingAddress?.street ||
                        quotation.billingAddress?.address ||
                        quotation.billingAddress?.street ||
                        customer?.billingAddress?.address ||
                        customer?.billingAddress?.street ||
                        'N/A'}
                    </p>
                    {quotation.shippingAddress?.landmark && (
                      <p className="mt-1 text-xs text-gray-600 capitalize break-words">
                        Landmark: {quotation.shippingAddress.landmark}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">City</p>
                    <p className="capitalize break-words">
                      {renderLocation(quotation.shippingAddress?.city) ||
                        renderLocation(quotation.billingAddress?.city) ||
                        renderLocation(customer?.billingAddress?.city) ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">State</p>
                    <p className="capitalize break-words">
                      {renderLocation(quotation.shippingAddress?.state) ||
                        renderLocation(quotation.billingAddress?.state) ||
                        renderLocation(customer?.billingAddress?.state) ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Pincode</p>
                    <p className="break-words">
                      {quotation.shippingAddress?.pincode ||
                        quotation.billingAddress?.pincode ||
                        customer?.billingAddress?.pincode ||
                        'N/A'}
                    </p>
                  </div>
                  {quotation.shippingAddress?.deliveryInstructions && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">
                        Delivery Instructions
                      </p>
                      <p className="text-gray-700 capitalize break-words">
                        {quotation.shippingAddress.deliveryInstructions}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Items Table */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Items</h2>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MRP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manufacturer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 break-words text-sm font-medium text-gray-900 capitalize">
                      {item.productName || 'N/A'}
                    </td>
                    <td className="px-4 py-3 break-words text-sm text-gray-600">{item.quantity || 0}</td>
                    <td className="px-4 py-3 break-words text-sm text-gray-600">₹{(item.mrp || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 break-words text-sm text-gray-600">
                      ₹{item.unitPrice?.toLocaleString('en-IN') || '0'}
                      {item.priceOverride && <span className="text-red-600 text-xs ml-1">(Override)</span>}
                    </td>
                    <td className="px-4 py-3 break-words text-sm text-gray-600">{formatDate(item.expiry, { includeTime: false }) || '-'}</td>
                    <td className="px-4 py-3 break-words text-sm text-gray-600">{item.manufacturing || '-'}</td>
                    <td className="px-4 py-3 break-words text-sm text-gray-600">{item.discount || 0}%</td>
                    <td className="px-4 py-3 break-words text-sm text-gray-600">{item.tax || 0}%</td>
                    <td className="px-4 py-3 break-words text-sm font-medium text-gray-900">₹{item.lineTotal?.toLocaleString('en-IN') || '0'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 break-words">Subtotal:</span>
                <span className="font-medium break-words">₹{subtotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 break-words">Total Discount:</span>
                <span className="font-medium text-green-600 break-words">-₹{discountAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 break-words">Taxable Amount:</span>
                <span className="font-medium break-words">₹{taxableAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 break-words">Total Tax:</span>
                <span className="font-medium break-words">₹{taxAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2">
                <span className="text-lg font-semibold text-gray-900 break-words">Grand Total:</span>
                <span className="text-lg font-bold text-agrosaf-green break-words">₹{grandTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        {quotation.terms && (() => {
          const termsText = quotation.terms;
          const shouldTruncate = termsText.length > 200;
          const displayText = showFullTerms || !shouldTruncate
            ? termsText
            : `${termsText.substring(0, 200)}...`;

          return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Terms & Conditions</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap break-words">{displayText}</p>
              </div>
              {shouldTruncate && (
                <button
                  onClick={() => setShowFullTerms(!showFullTerms)}
                  className="mt-4 text-sm font-medium text-agrosaf-green hover:text-agrosaf-darkGreen focus:outline-none"
                >
                  {showFullTerms ? 'Show Less' : 'Show More'}
                </button>
              )}
            </div>
          );
        })()}

        {/* Notes */}
        {quotation.notes && (() => {
          const notesText = quotation.notes;
          const shouldTruncate = notesText.length > 200;
          const displayText = showFullNotes || !shouldTruncate
            ? notesText
            : `${notesText.substring(0, 200)}...`;

          return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap break-words">{displayText}</p>
              </div>
              {shouldTruncate && (
                <button
                  onClick={() => setShowFullNotes(!showFullNotes)}
                  className="mt-4 text-sm font-medium text-agrosaf-green hover:text-agrosaf-darkGreen focus:outline-none"
                >
                  {showFullNotes ? 'Show Less' : 'Show More'}
                </button>
              )}
            </div>
          );
        })()}

        {/* Action Buttons */}
        {canShowActions && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="primary"
                icon={<CheckCircle className="h-5 w-5" />}
                onClick={handleAcceptClick}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
              >
                {submitting ? 'Processing...' : 'Accept Quotation'}
              </Button>
              <Button
                variant="outline"
                icon={<XCircle className="h-5 w-5" />}
                onClick={handleRejectClick}
                disabled={submitting}
                className="border-red-300 text-red-700 hover:bg-red-50 w-full sm:w-auto"
              >
                {submitting ? 'Processing...' : 'Reject Quotation'}
              </Button>
            </div>
          </div>
        )}

        {/* Accept Confirmation Modal */}
        {showAcceptConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 break-words">Confirm Acceptance</h3>
              <p className="text-gray-700 mb-6 break-words">
                Are you sure you want to accept this quotation? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowAcceptConfirm(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAccept}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  icon={submitting ? <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" /> : <CheckCircle className="h-4 w-4" />}
                >
                  {submitting ? 'Processing...' : 'Accept'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Reason Modal */}
        {showRejectReasonModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 break-words">Reject Quotation</h3>
              <p className="text-gray-700 mb-4 break-words">
                Please provide a reason for rejecting this quotation. This action cannot be undone.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please explain why you're rejecting this quotation..."
                  rows={4}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  required
                />
                <div className="flex justify-end mt-1">
                  <span className={`text-xs ${rejectReason.length > 180 ? 'text-orange-600' : 'text-gray-500'}`}>
                    {rejectReason.length}/200 characters
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectReasonModal(false);
                    setRejectReason('');
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={submitting || rejectReason.trim().length === 0}
                  className="border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  icon={submitting ? <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" /> : <XCircle className="h-4 w-4" />}
                >
                  {submitting ? 'Processing...' : 'Reject Quotation'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicQuotationView;

